import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRoofTypes } from '@/hooks/useRoofTypes';
import {
  ArrowLeft, Save, Trash2, Phone, Mail, MapPin, Calendar, Tag,
  CheckCircle2, Loader2, AlertCircle, ExternalLink, PhoneCall,
} from 'lucide-react';
import PhoneCallScriptModal from '@/components/PhoneCallScriptModal';

const OUTCOME_OPTIONS = [
  '', 'New', 'Contacted', 'Awaiting Quote', 'Quoted', 'Promoted',
  'Won', 'Lost', 'Not Interested', 'Wrong Number', 'No Answer', 'Other',
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const roofTypes = useRoofTypes(api);
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState({});
  const [dd, setDd] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [scriptOpen, setScriptOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [leadRes, ddRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get('/dropdowns'),
      ]);
      setLead(leadRes.data);
      setForm(leadRes.data);
      setDd(ddRes.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load lead');
    }
    setLoading(false);
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload._id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.promoted_at;
      delete payload.promoted_client_id;
      delete payload.promoted_property_id;
      delete payload.promoted_appraisal_id;
      delete payload.promoted_by;
      await api.put(`/leads/${id}`, payload);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this lead?')) return;
    await api.delete(`/leads/${id}`);
    navigate('/leads');
  };

  const handlePromote = async () => {
    if (!window.confirm('Promote this lead to a Client + Property + Appraisal?\n\nThe lead will be closed and the new appraisal will be created in "Client Data" status. You can then book a date from the calendar.')) return;
    setPromoting(true);
    try {
      const { data } = await api.post(`/leads/${id}/promote`);
      if (data?.appraisal_id) {
        navigate(`/appraisals/${data.appraisal_id}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Promotion failed');
    }
    setPromoting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading lead…
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12 space-y-3">
        <AlertCircle size={32} className="mx-auto text-amber-500" />
        <p className="text-slate-500">Lead not found</p>
        <Link to="/leads" className="text-[#D35400] text-sm font-semibold hover:underline">← Back to Leads</Link>
      </div>
    );
  }

  const promoted = !!lead.promoted_appraisal_id;

  return (
    <div data-testid="lead-detail-page" className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <button data-testid="lead-detail-back" onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft size={14} /> Back to Leads
        </button>
        <div className="flex items-center gap-2">
          {/* Phone-qualifier script — opens a guided popup that captures the
              pre-qualification answers in one go and writes them back to the
              lead form on save. Doubles as a printable hard-copy script. */}
          <button
            data-testid="lead-phone-script-launch"
            onClick={() => setScriptOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#D35400] text-[#D35400] hover:bg-[#D35400]/10 text-xs font-semibold rounded-md shadow-sm transition-colors"
            title="Open phone qualifier script — guided questions + answers bind to this lead"
          >
            <PhoneCall size={13} /> Phone Call Script
          </button>
          {promoted ? (
            <Link
              data-testid="lead-detail-view-appraisal"
              to={`/appraisals/${lead.promoted_appraisal_id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md shadow-sm"
            >
              <ExternalLink size={13} /> View promoted appraisal
            </Link>
          ) : (
            <button
              data-testid="lead-detail-promote"
              onClick={handlePromote}
              disabled={promoting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md shadow-sm disabled:opacity-40"
            >
              {promoting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Promote to Appraisal
            </button>
          )}
          <button data-testid="lead-detail-save" onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white text-xs font-semibold rounded-md disabled:opacity-40">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save changes
          </button>
          <button data-testid="lead-detail-delete" onClick={handleDelete} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold rounded-md">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>

      <header className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold ring-1 ring-amber-200">
          {(lead.contact_first_name?.[0] || '?')}{(lead.contact_surname?.[0] || '')}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope]">
            {lead.contact_first_name} {lead.contact_surname}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
            {lead.lead_source && <span className="flex items-center gap-1"><Tag size={11} />{lead.lead_source}</span>}
            {lead.lead_type && <span className="flex items-center gap-1"><PhoneCall size={11} />{lead.lead_type}</span>}
            {lead.event_name && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[10px] font-semibold ring-1 ring-purple-200">{lead.event_name}</span>}
            {lead.lead_date && <span className="flex items-center gap-1"><Calendar size={11} />{lead.lead_date}</span>}
            {lead.contact_suburb && <span className="flex items-center gap-1"><MapPin size={11} />{lead.contact_suburb}{lead.contact_city ? `, ${lead.contact_city}` : ''}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {promoted ? (
            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-full ring-1 ring-emerald-200">
              ✓ Promoted to Appraisal
            </span>
          ) : (
            <span className={`px-2 py-1 text-[10px] font-semibold rounded-full ring-1 ${lead.lead_closed ? 'bg-slate-100 text-slate-500 ring-slate-200' : 'bg-amber-50 text-amber-700 ring-amber-200'}`}>
              {lead.lead_closed ? 'Closed' : 'Open'}
            </span>
          )}
          {savedFlash && <span className="text-xs text-emerald-600 font-semibold">Saved ✓</span>}
        </div>
      </header>

      {error && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Contact + Marketing source */}
        <div className="lg:col-span-2 space-y-4">
          {/* Contact details */}
          <Section title="Contact details">
            <Grid>
              <Field label="First name"><Input value={form.contact_first_name} onChange={v => setForm({ ...form, contact_first_name: v })} testid="lead-first-name" /></Field>
              <Field label="Surname"><Input value={form.contact_surname} onChange={v => setForm({ ...form, contact_surname: v })} testid="lead-surname" /></Field>
              <Field label="Phone"><Input value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} icon={<Phone size={11} />} testid="lead-phone" /></Field>
              <Field label="Mobile"><Input value={form.contact_mobile} onChange={v => setForm({ ...form, contact_mobile: v })} icon={<Phone size={11} />} testid="lead-mobile" /></Field>
              <Field label="Email" wide><Input value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} icon={<Mail size={11} />} testid="lead-email" /></Field>
              <Field label="Street" wide><Input value={form.contact_street} onChange={v => setForm({ ...form, contact_street: v })} testid="lead-street" /></Field>
              <Field label="Suburb"><Input value={form.contact_suburb} onChange={v => setForm({ ...form, contact_suburb: v })} testid="lead-suburb" /></Field>
              <Field label="City"><Input value={form.contact_city} onChange={v => setForm({ ...form, contact_city: v })} testid="lead-city" /></Field>
            </Grid>
          </Section>

          {/* Marketing attribution */}
          <Section title="Marketing source" subtitle="Where did this lead come from?">
            <Grid>
              <Field label="Lead type">
                <Select value={form.lead_type} onChange={v => setForm({ ...form, lead_type: v })} options={dd.lead_types || []} testid="lead-type" />
              </Field>
              <Field label="Source channel">
                <Select value={form.lead_source} onChange={v => setForm({ ...form, lead_source: v })} options={dd.lead_sources || []} testid="lead-source" />
              </Field>
              <Field label="Event / Campaign" wide>
                <Input value={form.event_name} onChange={v => setForm({ ...form, event_name: v })} placeholder='e.g. "Tonic Home Show 2026", "Letterbox Drop — Hamilton East"' testid="lead-event-name" />
              </Field>
              <Field label="Campaign ID">
                <Input value={form.campaign_id} onChange={v => setForm({ ...form, campaign_id: v })} placeholder="Optional reference" testid="lead-campaign-id" />
              </Field>
              <Field label="Lead date">
                <Input type="date" value={form.lead_date} onChange={v => setForm({ ...form, lead_date: v })} testid="lead-date" />
              </Field>
            </Grid>
          </Section>

          {/* Requirements + notes */}
          <Section title="Notes & requirements">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Requirements</label>
            <textarea
              data-testid="lead-requirements"
              value={form.lead_requirements || ''}
              onChange={e => setForm({ ...form, lead_requirements: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-[#D35400] focus:outline-none"
            />
            {/* Pre-qualification fields — phone staff ask these BEFORE booking
                so the inspector arrives with the right gear. Auto-flows into
                roof_details on promote. */}
            <Grid>
              <Field label="Pre-qualified roof type">
                <select
                  data-testid="lead-pre-qualified-roof-type"
                  value={form.pre_qualified_roof_type || ''}
                  onChange={e => setForm({ ...form, pre_qualified_roof_type: e.target.value })}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:border-[#D35400] focus:outline-none bg-white"
                >
                  <option value="">— Ask client —</option>
                  {roofTypes.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                  {form.pre_qualified_roof_type && form.pre_qualified_roof_type !== 'Other' && !roofTypes.includes(form.pre_qualified_roof_type) && (
                    <option value={form.pre_qualified_roof_type}>{form.pre_qualified_roof_type} (old)</option>
                  )}
                  <option value="Other">Other / Unsure</option>
                </select>
              </Field>
              <Field label="House age (approx.)">
                <Input
                  value={form.pre_qualified_house_age || ''}
                  onChange={v => setForm({ ...form, pre_qualified_house_age: v })}
                  placeholder="e.g. Built 1972, ~30 yrs"
                  testid="lead-pre-qualified-house-age"
                />
              </Field>
            </Grid>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 mt-3">Follow-up notes</label>
            <textarea
              data-testid="lead-notes"
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder="Date-stamped chronological notes…"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-[#D35400] focus:outline-none"
            />
          </Section>
        </div>

        {/* RIGHT: Pipeline */}
        <div className="space-y-4">
          <Section title="Pipeline">
            <Grid>
              <Field label="Assigned to" wide>
                <Input value={form.assigned_to} onChange={v => setForm({ ...form, assigned_to: v })} placeholder="Staff name" testid="lead-assigned-to" />
              </Field>
              <Field label="Next follow-up" wide>
                <Input type="date" value={form.next_follow_up_date} onChange={v => setForm({ ...form, next_follow_up_date: v })} testid="lead-next-follow-up" />
              </Field>
              <Field label="Outcome" wide>
                <Select value={form.outcome} onChange={v => setForm({ ...form, outcome: v })} options={OUTCOME_OPTIONS} testid="lead-outcome" />
              </Field>
              <Field label="Closed" wide>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="lead-closed"
                    checked={!!form.lead_closed}
                    onChange={e => setForm({ ...form, lead_closed: e.target.checked })}
                    className="w-4 h-4 accent-[#D35400] rounded"
                  />
                  <span className="text-slate-700">Mark this lead as closed</span>
                </label>
              </Field>
            </Grid>
          </Section>

          {promoted && (
            <Section title="Promotion record" subtitle="Linked downstream records">
              <div className="text-xs space-y-1.5">
                <Link to={`/clients/${lead.promoted_client_id}`} className="block text-[#D35400] hover:underline">→ Client</Link>
                <Link to={`/properties/${lead.promoted_property_id}`} className="block text-[#D35400] hover:underline">→ Property</Link>
                <Link to={`/appraisals/${lead.promoted_appraisal_id}`} className="block text-[#D35400] hover:underline">→ Appraisal</Link>
                {lead.promoted_at && <p className="text-slate-400 pt-1.5 border-t border-slate-100 mt-1.5">Promoted {new Date(lead.promoted_at).toLocaleString('en-NZ')}{lead.promoted_by ? ` by ${lead.promoted_by}` : ''}</p>}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Phone-qualifier modal — applies its captured answers back to the
          form state when the operator clicks "Save to lead". Operator still
          needs to click "Save changes" in the toolbar to persist to the API. */}
      <PhoneCallScriptModal
        open={scriptOpen}
        form={form}
        onApply={(patch) => {
          setForm((f) => ({ ...f, ...patch }));
          setSavedFlash(false);
        }}
        onClose={() => setScriptOpen(false)}
      />
    </div>
  );
}

const Section = ({ title, subtitle, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="mb-3">
      <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;

const Field = ({ label, children, wide }) => (
  <div className={wide ? 'md:col-span-2' : ''}>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, type = 'text', placeholder, icon, testid }) => (
  <div className="relative">
    {icon && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>}
    <input
      type={type}
      data-testid={testid}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full ${icon ? 'pl-7' : 'pl-3'} pr-3 py-2 border border-slate-200 rounded-md text-sm focus:border-[#D35400] focus:outline-none`}
    />
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:border-[#D35400] focus:outline-none"
  >
    <option value="">— Select —</option>
    {options.map(o => <option key={o || 'blank'} value={o}>{o || '(none)'}</option>)}
  </select>
);
